<script setup lang="ts" generic="T">
import { computed } from 'vue';

import { ProductAccessLabel } from '@/views/Accounts/components';
import { TableCell } from '@/components/ui/Table';
import { SingleCell } from '@/components/custom/CustomTable';

import { cn } from '@/lib/utils';

import {
  type CustomTableColumn,
  PRODUCT_ACCESS_COLUMN,
  renderRow,
  SELECT_COLUMN
} from '@/constants/table';

import type { OrganizationUserPresetSimplified } from '@/types/organizationUserPresets';
import { Checkbox } from '@/components/custom';

const props = defineProps<{
  row: T;
  isModal?: boolean;
  updatedRow?: T;
  column: CustomTableColumn;
  isSelectable?: boolean;
  isSelected?: boolean;
  rowKey?: keyof T;
}>();

const isColumnChanged = computed(() => {
  return (
    props.updatedRow &&
    props.updatedRow[props.column.value.key as keyof T] !==
      props.row[props.column.value.key as keyof T]
  );
});

const rowKey = props.rowKey ?? ('id' as keyof T);

const emit = defineEmits<{
  (e: 'toggleSelected', rowId: unknown, checked: boolean): void;
}>();

function onCheckboxChange(event: Event) {
  const checked = (event.target as HTMLInputElement).checked;
  emit('toggleSelected', props.row[rowKey], checked);
}
</script>

<template>
  <TableCell v-if="column.value.key === SELECT_COLUMN">
    <Checkbox
      :checked="isSelected"
      test-id="custom-table-cell-checkbox"
      @change="onCheckboxChange"
    />
  </TableCell>

  <TableCell
    v-else
    :class="
      cn(
        'break-all px-5 py-3 text-sm',
        column.isExtended ? 'min-w-90' : 'min-w-56'
      )
    "
  >
    <ProductAccessLabel
      v-if="!isModal && column.value.key === PRODUCT_ACCESS_COLUMN"
      :idea-database-roles="
        (row as OrganizationUserPresetSimplified).ideaDatabaseRoles
      "
      :patent-platform-role="
        (row as OrganizationUserPresetSimplified).patentsUserType
      "
      :is-id-enabled="
        (row as OrganizationUserPresetSimplified).ideaDatabaseRoles?.length
          ? 1
          : 0
      "
      :is-patent-platform-enabled="
        (row as OrganizationUserPresetSimplified).patentsUserType ? 1 : 0
      "
    />
    <span
      v-else-if="!isColumnChanged"
      data-testid="custom-table-unchanged-cell"
      >{{ renderRow((row as T)[column.value.key as keyof T]) }}</span
    >
    <div v-else>
      <SingleCell
        :cell="row[column.value.key as keyof typeof row]"
        test-id="custom-table-removed-cell"
        :updated-cell="
          (updatedRow as T)[column.value.key as keyof typeof updatedRow]
        "
        :column-key="column.value.key"
        class="text-red-500 line-through"
      />
      <span> -> </span>
      <SingleCell
        test-id="custom-table-added-cell"
        :cell="(updatedRow as T)[column.value.key as keyof typeof updatedRow]"
        :updated-cell="row[column.value.key as keyof typeof row]"
        :column-key="column.value.key"
        class="text-green-500"
      />
    </div>
  </TableCell>
</template>
